'use client';

import { useModals } from '@/contexts/ModalContext';
import { useLocale, useTranslations } from 'next-intl';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCallback, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useUserData } from '@/contexts/LoginContext';
import {
  PatchTelegramUsersLanguageDtoLanguageEnum,
  TelegramCurrencyResponse,
  TelegramCurrencyResponseCurrencyEnum,
  useTelegramCurrenciesFindMany,
  useTelegramUsersPatch,
  useTelegramUsersPatchLanguage,
} from '@/generated';
import { useUpdateGlobalUser } from '@/hooks/useUpdateGlobalUser';

import { useRouter } from '@/hooks/useRouter';
import { usePathname } from '@/navigation';
import { useCurrency, useSetCurrency } from '@/contexts/CookieContext';
import { routing } from '@/i18n/routing';
import { AppLocale, appLocales, defaultAppLocale, getLocaleConfig, isSupportedLocale } from '@/i18n/locales';

type Props = {
  onOk?: () => void;
};

export const GeneralSettingsModal: React.FC<Props> = ({ onOk }) => {
  const modals = useModals();
  const t = useTranslations('modals');
  const sysLocale = useLocale();
  const onClose = useCallback(() => {
    onOk?.();
    modals.close(`general-settings`);
  }, [modals, onOk]);

  const userData = useUserData();

  const pathName = usePathname();

  const router = useRouter();

  const setCurrency = useSetCurrency();

  const cookieCurrency = useCurrency();

  const updateGlobalUser = useUpdateGlobalUser();

  const [locale, setLocale] = useState<AppLocale>(isSupportedLocale(sysLocale) ? sysLocale : defaultAppLocale);

  const [isLoading, setIsLoading] = useState(false);

  const [localCurrency, setLocalCurrency] = useState<TelegramCurrencyResponseCurrencyEnum | undefined>(cookieCurrency);

  const { data: cur = [], isLoading: isCurrenciesLoading } = useTelegramCurrenciesFindMany();

  const { mutateAsync: patchUser } = useTelegramUsersPatch({
    client: {
      handle401: false,
    },
  });

  const { mutateAsync: patchUserLang } = useTelegramUsersPatchLanguage({
    client: {
      handle401: false,
    },
  });

  const updateCurrencyUser = useCallback(
    async (newCurrency: TelegramCurrencyResponseCurrencyEnum) => {
      await patchUser({
        data: {
          currency: newCurrency,
        },
      });
      updateGlobalUser();
    },
    [updateGlobalUser, patchUser],
  );

  const currencies = cur as TelegramCurrencyResponse[];

  const onClickLocale = useCallback((newLocale: string) => {
    if (!isSupportedLocale(newLocale)) {
      return;
    }
    setLocale(newLocale);
  }, []);

  const updateCurrency = useCallback((newCurrency: TelegramCurrencyResponseCurrencyEnum) => {
    setLocalCurrency(newCurrency);
  }, []);

  const hardReloadToLocale = useCallback(
    (nextLocale: AppLocale) => {
      router.push(pathName, {
        locale: nextLocale,
      });

      const basePath = pathName === '/' ? '' : pathName;
      const localizedPath = nextLocale === routing.defaultLocale ? basePath || '/' : `/${nextLocale}${basePath}`;
      window.location.replace(`${localizedPath}${window.location.search}${window.location.hash}`);
    },
    [pathName, router],
  );

  const onClickSave = useCallback(async () => {
    setIsLoading(true);
    let shouldReloadPage = false;
    try {
      if (localCurrency !== userData?.currency && localCurrency) {
        shouldReloadPage = true;
        setCurrency(localCurrency);
        if (userData) {
          await updateCurrencyUser(localCurrency);
        }
      }

      if (locale !== sysLocale) {
        shouldReloadPage = false;
        try {
          await patchUserLang({ data: { language: locale as PatchTelegramUsersLanguageDtoLanguageEnum } });
          updateGlobalUser();
        } catch {}
        hardReloadToLocale(locale);
        return;
      }
    } catch {}

    if (shouldReloadPage) {
      window.location.reload();
    }
    setIsLoading(false);
    onClose();
  }, [
    localCurrency,
    locale,
    userData,
    updateGlobalUser,
    updateCurrencyUser,
    onClose,
    sysLocale,
    patchUserLang,
    setCurrency,
    hardReloadToLocale,
    router,
  ]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogTitle className='sr-only'>{t('generalSettings.select_language')}</DialogTitle>

        <div className='flex flex-col gap-[8px]'>
          <span className='text-m-h text-start'>{t('generalSettings.select_language')}</span>

          {isLoading ? (
            <Skeleton className='h-[44px] w-full rounded-[12px]' />
          ) : (
            <Select value={locale} onValueChange={(value) => onClickLocale(value as typeof locale)}>
              <SelectTrigger className='w-full h-[44px]! border-primary-stroke-day bg-white text-[16px]'>
                <SelectValue placeholder={t('generalSettings.language')} />
              </SelectTrigger>
              <SelectContent position='popper'>
                <SelectGroup>
                  <SelectLabel>{t('generalSettings.select_language')}</SelectLabel>
                  {appLocales.map((language) => (
                    <SelectItem key={language} value={language}>
                      {getLocaleConfig(language).nativeName}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </div>

        <div className='flex flex-col gap-[8px]'>
          <span className='text-m-h text-start'>{t('generalSettings.select_currency')}</span>

          {isCurrenciesLoading || isLoading ? (
            <Skeleton className='h-[44px] w-full rounded-[12px]' />
          ) : (
            <Select value={localCurrency || userData?.currency || undefined} onValueChange={updateCurrency}>
              <SelectTrigger className='w-full h-[44px]! border-primary-stroke-day bg-white text-[16px]'>
                <SelectValue placeholder={t('generalSettings.label_currency')} />
              </SelectTrigger>
              <SelectContent position='popper'>
                <SelectGroup>
                  <SelectLabel>{t('generalSettings.select_currency')}</SelectLabel>
                  {currencies.map((elem) => (
                    <SelectItem key={elem.currency} value={elem.currency}>
                      {elem.name + ' ' + elem.sign}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </div>

        {isLoading ? (
          <Skeleton className='w-full h-[44px] rounded-[12px]' />
        ) : (
          <Button
            variant='default'
            onClick={onClickSave}
            className='h-auto p-[15px] w-full gap-[8px] text-white rounded-[12px] text-m'
          >
            {t('generalSettings.save')}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
